/**
 * El panel de venta rápida dentro de la solapa «Ventas» del cliente.
 *
 * Es la mitad que va **arriba** de la grilla de ventas del cliente, y son tres
 * cosas en este orden, que es el que pidió el dueño:
 *
 *  1. **Los dos totales** de lo que debe, separados y rotulados: facturado y sin
 *     pagar (exigible) contra vendido y sin facturar (comprometido). Nunca
 *     sumados — ver `TotalesDelClientePanel`.
 *  2. El botón **«Vender productos»**, que abre el mismo selector múltiple que
 *     el acceso rápido del inicio. No hay dos selectores.
 *  3. La aclaración de qué pasa al confirmar (nada se factura) y de dónde está
 *     el botón que sí factura.
 *
 * La grilla con lo vendido, su vencimiento y su estado ya la dibuja el renderer
 * genérico debajo, desde la metadata (`base.partner.subscriptions`): este panel
 * no la duplica.
 *
 * Los totales salen de la acción de sólo lectura `base.partner.lo_que_debe`. Se
 * piden al backend y no se calculan acá a propósito: uno de los dos números es
 * la cuenta corriente, que la calcula `invoicing`, y una segunda suma del mismo
 * saldo del lado del cliente terminaría dando dos respuestas a la misma
 * pregunta.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { IconAlert, IconShoppingCart } from '@/components/shell/icons';
import { Button } from '@/components/shell/primitives';
import { ApiError, api } from '@/lib/api';
import type { ActionResult } from '@/lib/types';
import { useEstadoDeVentaRapida, useSalidaAEmpresaQueVende } from '@/views/EmpresaQueVende';
import { AvisoDeBloqueo, BotonBloqueado, useGuardadoDeLaFicha } from '@/views/MotivoBloqueo';
import { useInvalidateData } from '@/views/queries';
import {
  TotalesDelClientePanel,
  VentaRapidaDialog,
  type TotalesDelCliente,
} from '@/views/VentaRapidaDialog';

const MODELO = 'base.partner';

function datosDe(resultado: ActionResult | undefined): TotalesDelCliente | null {
  const resultados = (resultado as { results?: unknown } | undefined)?.results;
  if (!Array.isArray(resultados) || resultados.length === 0) return null;
  const datos = (resultados[0] as { data?: unknown }).data;
  if (datos === null || typeof datos !== 'object') return null;
  const d = datos as Record<string, unknown>;
  if (typeof d.invoiced_unpaid !== 'string' || typeof d.committed_unbilled !== 'string') return null;
  return datos as unknown as TotalesDelCliente;
}

export interface VentaRapidaPanelProps {
  /** El contacto cuya ficha se está mirando. `null` (alta sin guardar) no dibuja. */
  recordId: number | null;
}

export function VentaRapidaPanel({ recordId }: VentaRapidaPanelProps) {
  const [abierto, setAbierto] = useState(false);
  // El guardado de la ficha que contiene esta solapa. `null` si el panel se
  // dibujara fuera de un formulario: ahí no se ofrece la salida y listo.
  const guardado = useGuardadoDeLaFicha();
  const queryClient = useQueryClient();
  const invalidar = useInvalidateData();
  // ¿Se puede vender desde la empresa que está activa en el selector de arriba?
  // El motivo lo escribe el servidor; acá sólo se dibuja. Ver
  // `views/EmpresaQueVende`.
  const venta = useEstadoDeVentaRapida();
  const salidaDeEmpresa = useSalidaAEmpresaQueVende();

  const clave = ['venta-rapida-totales', recordId] as const;
  const totales = useQuery({
    queryKey: clave,
    enabled: recordId !== null,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const respuesta = await api.callAction(MODELO, recordId as number, 'lo_que_debe', {});
      return datosDe(respuesta);
    },
  });

  if (recordId === null) {
    // El texto ya era el correcto —nombra la causa y la salida— y lo que le
    // faltaba era el botón: la salida se enunciaba y no se ofrecía, y «Crear»
    // está arriba de todo, fuera de esta solapa. Ahora sale del mismo mecanismo
    // que los botones de la cabecera y que la grilla de renglones, con el mismo
    // gesto: se crea el contacto y nada más.
    return (
      <AvisoDeBloqueo
        className="mb-5"
        motivo="Guardá el contacto y vas a poder venderle productos desde acá."
        salida={
          guardado === null
            ? null
            : {
                etiqueta: 'Crear el contacto',
                aclaracion: 'Se crea el contacto y nada más. No vende ni factura nada.',
                ejecutar: () => {
                  guardado.guardar();
                },
                ...(guardado.guardando ? { corriendo: true } : {}),
              }
        }
      />
    );
  }

  // Sin permiso sobre la acción —un operador, un propietario— el panel no se
  // dibuja: sus números son la cuenta corriente del cliente y no son para
  // cualquier rol. Lo que sí cambió es que ANTES desaparecía sin dejar rastro, y
  // quien lo vio en la máquina de al lado no tenía cómo saber que le faltaba un
  // permiso y no que el sistema estuviera roto. La solapa sigue mostrando la
  // grilla de lo vendido, que es lo que ese rol sí puede ver.
  if (
    totales.isError &&
    totales.error instanceof ApiError &&
    (totales.error.isPermissionError || totales.error.status === 404)
  ) {
    return (
      <AvisoDeBloqueo
        className="mb-5"
        motivo="Tu usuario no tiene permiso para ver lo que este cliente debe ni para venderle desde acá. Abajo sí podés ver lo que se le vendió. Si necesitás el resto, pedíselo a quien administra los accesos."
      />
    );
  }

  return (
    <section aria-labelledby="venta-rapida-panel-titulo" className="mb-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3
          id="venta-rapida-panel-titulo"
          className="text-sm font-semibold uppercase tracking-wide text-fg-faint"
        >
          Lo que debe este cliente
        </h3>
        {venta.existe && !venta.habilitada ? (
          <BotonBloqueado
            variant="primary"
            size="sm"
            label={
              <>
                <IconShoppingCart className="mr-1 inline h-4 w-4" />
                Vender productos
              </>
            }
            motivo={venta.motivo}
            salida={salidaDeEmpresa}
          />
        ) : (
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              setAbierto(true);
            }}
          >
            <IconShoppingCart className="mr-1 h-4 w-4" />
            Vender productos
          </Button>
        )}
      </div>

      {/* El mismo motivo, también como cartel: el globo del botón lo cuenta a
          quien lo hoverea o lo enfoca, y esto lo cuenta a quien sólo mira. Va
          acá —en la solapa donde la persona fue a vender— y no arriba de todo
          con los demás avisos de la ficha. */}
      {venta.existe && !venta.habilitada && (
        <AvisoDeBloqueo
          className="mb-3"
          tono="atencion"
          motivo={venta.motivo}
          salida={salidaDeEmpresa}
        />
      )}

      {totales.isError ? (
        <div className="flex items-start gap-3 rounded-lg border border-line p-3">
          <IconAlert className="h-5 w-5 shrink-0 text-danger" />
          <p className="text-sm text-fg-muted">
            No se pudieron traer los totales.{' '}
            {totales.error instanceof ApiError ? totales.error.message : 'Reintentá en un momento.'}
          </p>
        </div>
      ) : totales.isPending ? (
        <p className="rounded-lg border border-line px-3 py-4 text-sm text-fg-muted">
          Calculando lo facturado y lo comprometido…
        </p>
      ) : (
        <TotalesDelClientePanel totales={totales.data ?? null} />
      )}

      <p className="mt-3 text-xs text-fg-faint">
        Son <strong>dos números distintos y no se suman</strong>: lo facturado ya tiene comprobante
        y se le puede reclamar; lo vendido y sin facturar todavía no. Para emitir el comprobante,
        abrí una fila de la grilla de abajo y usá «Armar factura en borrador»: queda en borrador
        para que una persona la revise y la confirme.
      </p>

      <VentaRapidaDialog
        partnerId={abierto ? recordId : null}
        onClose={() => {
          setAbierto(false);
        }}
        onDone={() => {
          void queryClient.invalidateQueries({ queryKey: clave });
          // La venta escribió filas de `sales.subscription`, que es lo que
          // dibuja la grilla de abajo y el botón «Le vendimos» de arriba.
          void invalidar('sales.subscription');
          void invalidar(MODELO);
        }}
      />
    </section>
  );
}
